import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'

import i18n from '@app/i18n'

import { useSessionEntity } from '@context/EntityContext'

import { getCompanyName } from '@utils/companyName.utils'

import { safeSessionStorage } from '@hooks/useStorage'

import { ampli } from '../../../ampli'
import type { WithdrawalPaymentTermsConditionsOpenedProperties } from '../../../ampli'

export type WithdrawalEntryPoint =
  | 'wallet'
  | 'balances'
  | 'transactions'
  | 'menu_shortcut'
  | 'direct_link'
  | 'payment_agent'

export type WithdrawalCurrentStep = 'wallet_method' | 'amount_terms' | 'preview'

type WithdrawalSharedContextProperties = Omit<
  WithdrawalPaymentTermsConditionsOpenedProperties,
  'current_step' | 'platform'
> & {
  current_step: WithdrawalCurrentStep
}

export interface WithdrawalStartProps {
  entry_point: WithdrawalEntryPoint
  source_page?: string
  preset_wallet_id?: string
}

export interface WithdrawalWalletSelectedProps {
  wallet_id: string
  currency: string
  selection_type: 'preset' | 'manual'
}

export interface WithdrawalMethodSelectedProps {
  method_id: string
  method_type: string
  provider: string
  is_last_used: boolean
}

export interface WithdrawalAmountEnteredProps {
  amount: number
  currency: string
  amount_source: 'manual' | 'preset'
}

export interface WithdrawalStepChangedProps {
  from: WithdrawalCurrentStep
  to: WithdrawalCurrentStep
  direction: 'forward' | 'backward'
}

type TrackerState = {
  id: string
  started: boolean
  ended: boolean
  submitted: boolean
  currentStep: WithdrawalCurrentStep
  entryPoint?: WithdrawalEntryPoint
  walletId?: string
  amount?: number
  currency?: string
  sourcePage?: string
  createdAt: number
}

const STORAGE_KEY = 'withdrawal_journey_v1'
const MAX_AGE_MS = 60 * 60 * 1000 // 1h

function canTrack(): boolean {
  return typeof window !== 'undefined' && ampli.isLoaded
}

function normalizeLocale(lang: string | undefined): string {
  return (lang ?? 'en').split('-')[0]
}

function safeReadState(): TrackerState | null {
  if (typeof window === 'undefined') return null

  const parsed = safeSessionStorage.getJSON<TrackerState>(STORAGE_KEY)
  if (!parsed?.id) return null

  if (Date.now() - parsed.createdAt > MAX_AGE_MS) {
    safeSessionStorage.removeItem(STORAGE_KEY)
    return null
  }

  return parsed
}

function writeState(next: TrackerState) {
  if (typeof window === 'undefined') return
  safeSessionStorage.setJSON(STORAGE_KEY, next)
}

function newState(): TrackerState {
  return {
    id: uuidv4(),
    started: false,
    ended: false,
    submitted: false,
    currentStep: 'wallet_method',
    createdAt: Date.now(),
  }
}

type GlobalStateRef = { current: TrackerState }
let __WITHDRAWAL_TRACKER_REF__: GlobalStateRef | null = null

function getGlobalStateRef(): GlobalStateRef {
  if (__WITHDRAWAL_TRACKER_REF__) return __WITHDRAWAL_TRACKER_REF__
  __WITHDRAWAL_TRACKER_REF__ = { current: safeReadState() ?? newState() }
  return __WITHDRAWAL_TRACKER_REF__
}

function buildSharedProps(
  s: TrackerState,
  pathname: string,
  entity: ReturnType<typeof useSessionEntity>,
  override?: Partial<WithdrawalSharedContextProperties>
): WithdrawalSharedContextProperties | null {
  if (!s.started || !s.entryPoint || !s.sourcePage) return null

  return {
    withdrawal_journey_id: s.id,
    entry_point: s.entryPoint,
    source_page: s.sourcePage,
    route: pathname,
    locale: normalizeLocale(i18n.language),
    tickmill_entity: getCompanyName(entity),
    current_step: s.currentStep,
    ...(override ?? {}),
  }
}

export function useWithdrawalTracker() {
  const location = useLocation()
  const entity = useSessionEntity()

  const stateRef = useRef(getGlobalStateRef()).current

  const persist = useCallback(() => writeState(stateRef.current), [stateRef])

  const getShared = useCallback(
    (override?: Partial<WithdrawalSharedContextProperties>) =>
      buildSharedProps(stateRef.current, location.pathname, entity, override),
    [entity, location.pathname, stateRef]
  )

  const start = useCallback(
    (props: WithdrawalStartProps) => {
      if (!canTrack()) return
      const s = stateRef.current

      const base = !s.started || s.ended ? newState() : s
      if (base.started && !base.ended) return

      stateRef.current = {
        ...base,
        started: true,
        ended: false,
        submitted: false,
        entryPoint: props.entry_point,
        sourcePage: props.source_page ?? location.pathname,
        currentStep: 'wallet_method',
        createdAt: Date.now(),
      }
      persist()

      const shared = getShared({ current_step: 'wallet_method' })
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_started',
        event_properties: {
          ...shared,
          has_preset_wallet: !!props.preset_wallet_id,
          preset_wallet_id: props.preset_wallet_id,
        },
      })
    },
    [getShared, location.pathname, persist, stateRef]
  )

  const walletSelected = useCallback(
    (props: WithdrawalWalletSelectedProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return
      if (s.walletId === props.wallet_id) return

      stateRef.current = { ...s, walletId: props.wallet_id }
      persist()

      const shared = getShared()
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_wallet_selected',
        event_properties: {
          ...shared,
          wallet_id: props.wallet_id,
          currency: props.currency,
          selection_type: props.selection_type,
        },
      })
    },
    [getShared, persist, stateRef]
  )

  const methodSelected = useCallback(
    (props: WithdrawalMethodSelectedProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      const shared = getShared()
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_method_selected',
        event_properties: {
          ...shared,
          method_id: props.method_id,
          method_type: props.method_type,
          provider: props.provider,
          is_last_used: props.is_last_used,
        },
      })
    },
    [getShared, stateRef]
  )

  const stepChanged = useCallback(
    (props: WithdrawalStepChangedProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      stateRef.current = { ...s, currentStep: props.to }
      persist()

      const shared = getShared({ current_step: props.to })
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_step_changed',
        event_properties: {
          ...shared,
          from_step: props.from,
          to_step: props.to,
          navigation_direction: props.direction,
        },
      })
    },
    [getShared, persist, stateRef]
  )

  const amountEntered = useCallback(
    (props: WithdrawalAmountEnteredProps) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return
      if (s.amount === props.amount && s.currency === props.currency) return

      stateRef.current = { ...s, amount: props.amount, currency: props.currency }
      persist()

      const shared = getShared()
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_amount_entered',
        event_properties: {
          ...shared,
          amount: props.amount,
          currency: props.currency,
          amount_source: props.amount_source,
        },
      })
    },
    [getShared, persist, stateRef]
  )

  const paymentTermsConditionsOpened = useCallback(() => {
    if (!canTrack()) return
    const s = stateRef.current
    if (!s.started || s.ended) return

    const shared = getShared()
    if (!shared) return

    ampli.withdrawalPaymentTermsConditionsOpened({
      ...shared,
      current_step: 'amount_terms',
    })
  }, [getShared, stateRef])

  const powerOfAttorneyAccepted = useCallback(() => {
    if (!canTrack()) return
    const s = stateRef.current
    if (!s.started || s.ended) return

    const shared = getShared()
    if (!shared) return

    ampli.track({
      event_type: 'withdrawal_terms_accepted',
      event_properties: { ...shared, checkbox_type: 'power_of_attorney' },
    })
  }, [getShared, stateRef])

  const submitted = useCallback(
    (props: { withdrawal_id: string }) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      const shared = getShared()
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_submitted',
        event_properties: { ...shared, withdrawal_id: props.withdrawal_id },
      })

      stateRef.current = { ...s, ended: true, submitted: true }
      persist()
    },
    [getShared, persist, stateRef]
  )

  const failed = useCallback(
    (props: { message: string }) => {
      if (!canTrack()) return
      const s = stateRef.current
      if (!s.started || s.ended) return

      const shared = getShared()
      if (!shared) return

      ampli.track({
        event_type: 'withdrawal_failed',
        event_properties: { ...shared, message: props.message },
      })

      stateRef.current = { ...s, ended: true, submitted: false }
      persist()
    },
    [getShared, persist, stateRef]
  )

  const abandoned = useCallback(() => {
    if (!canTrack()) return
    const s = stateRef.current
    if (!s.started || s.ended || s.submitted) return

    ampli.track({
      event_type: 'withdrawal_abandoned',
      event_properties: { current_step: s.currentStep },
    })

    stateRef.current = { ...s, ended: true }
    persist()
  }, [persist, stateRef])

  return useMemo(
    () => ({
      start,
      walletSelected,
      methodSelected,
      stepChanged,
      amountEntered,
      paymentTermsConditionsOpened,
      powerOfAttorneyAccepted,
      submitted,
      failed,
      abandoned,
      getState: () => stateRef.current,
    }),
    [
      start,
      walletSelected,
      methodSelected,
      paymentTermsConditionsOpened,
      powerOfAttorneyAccepted,
      stepChanged,
      amountEntered,
      submitted,
      failed,
      abandoned,
      stateRef,
    ]
  )
}

const WITHDRAWAL_ROUTES_REGEX = /\/withdrawal($|\/)/

export function useWithdrawalJourneyAutoAbandon() {
  const location = useLocation()
  const { abandoned, getState } = useWithdrawalTracker()

  const isWithdrawalRoute = useCallback((path: string) => WITHDRAWAL_ROUTES_REGEX.test(path), [])

  const prevWasWithdrawalRef = useRef<boolean>(isWithdrawalRoute(location.pathname))

  useEffect(() => {
    const curr = location.pathname
    const isWithdrawalNow = isWithdrawalRoute(curr)
    const wasWithdrawal = prevWasWithdrawalRef.current

    prevWasWithdrawalRef.current = isWithdrawalNow

    if (!wasWithdrawal || isWithdrawalNow) return

    const s = getState()
    if (!s.started || s.ended || s.submitted) return

    abandoned()
  }, [location.pathname, isWithdrawalRoute, abandoned, getState])
}
